import { z } from "zod";
import { resolvableCtaPathSchema } from "./deep-links";
import {
  localizedTextSchema,
  svRequiredLocalizedTextSchema,
} from "./intake-template";

// TES-158/159 (Flows) — scheduled, assignable content journeys. Shared contract
// across api ⟷ admin ⟷ mobile, mirroring the intake-template style: copy is
// carried inline as `{ sv, en }` (editable template data, not i18n keys) and a
// flow is a versioned draft/published template authored in the admin portal.
//
// A flow is an ordered set of FlowItems placed on relative day offsets (day 0 =
// the assignment anchor). Each item is *content* that can appear on one or more
// `surfaces` (see FLOW_SURFACES / RENDERED_SURFACES below) — so a single item
// can be a §2 home_now object AND a push, etc. No PII; template definition only.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

// TES-285 (J4) — the addressable surface taxonomy. Stored as free-text
// String[] on FlowItem/FlowAssignmentItem (no DB enum), so widening this list
// is a contracts-only change — no migration. `home_card` stays permanently as
// a legacy alias so every already-published flow + materialized item keeps
// validating. `chat_message` remains the MODULE TYPE (owns the content shape +
// the TES-233 approval pipeline); coach_chat/doctor_chat are the surfaces
// derived from its attribution.
//
// TES-351 — LIVE vs INERT: after the TES-311 home rework, only the surfaces in
// RENDERED_SURFACES below actually reach a patient. Everything else validates
// and materializes but renders NOWHERE today — content placed only on an inert
// surface is invisible. Authoring UIs + import validation should steer to the
// rendered set (the enum keeps the inert values so existing rows round-trip).
export const FLOW_SURFACES = [
  // Home zones (map 1:1 to the TES-311 home sections)
  "home_greeting", // §1 Personlig orientering — INERT (greeting is /home-summary)
  "home_now", // §2 "Vad händer just nu" (the one prioritized object — TES-286)
  "home_for_you", // §5 "För dig idag" — INERT (served by /home-summary today)
  "home_weekly_checkin", // §3 Veckoincheckning — INERT (§3 is derived, not authored)
  "home_wellness_preview", // §6 "Din plan" / wellness preview — INERT
  // TES-866 — a TITLED LIST of article entries on Home, rendered as its own
  // section. Live (see RENDERED_SURFACES).
  //
  // It exists because none of the surfaces above can express it. `home_now`
  // is ONE prioritised object (TES-286), so seven article cards there become
  // seven competing cards rather than a section. `home_for_you` is inert, and
  // `packageTheme`'s `override_for_you_only` takes over precisely the strip
  // labelled "För dig idag", which CP-60/61 canon says this section is not.
  //
  // Unlike `home_now` this surface carries no priority and no CTA of its own:
  // every entry opens an article. A package that wants an action still uses
  // `home_now`; the two are additive, and a package may author both.
  "home_questions",
  // In-app pages / lists — all INERT (no consumer reads these yet)
  "task_list",
  "lab_results",
  "health_history",
  "wellness_program",
  "feedback_card",
  // Chat (surface derived from chat_message attribution) — delivery is owned
  // by the chat_message MODULE TYPE's draft pipeline, not the surface tag.
  "coach_chat",
  "doctor_chat",
  // Delivery channels
  "push",
  "sms", // authorable but inert for MVP (no sender yet)
  "email",
  // Legacy generic — KEEP for existing rows; the TES-311 rework retired its
  // renderer (TodaySection/FlowCards), so it is INERT on mobile. home_now is
  // the canonical home action surface now (TES-351).
  "home_card",
] as const;
export const flowSurfaceSchema = z.enum(FLOW_SURFACES);
export type FlowSurface = z.infer<typeof flowSurfaceSchema>;

// TES-351 — the surfaces with a live end-to-end render/delivery path. Used by
// authoring UIs (picker) + import validation to keep authored content visible.
// `chat_message`-module content delivers via the chat draft pipeline regardless
// of surface tags, so the chat surfaces are intentionally absent here.
export const RENDERED_SURFACES = [
  "home_now",
  // TES-866 — live since the Home question section landed.
  "home_questions",
  "push",
  "email",
] as const;
export type RenderedSurface = (typeof RENDERED_SURFACES)[number];

// TES-285 — per-module approval (generalizes the chat-only draft approval).
export const FLOW_APPROVER_ROLES = ["coach", "doctor"] as const;
export const flowApproverRoleSchema = z.enum(FLOW_APPROVER_ROLES);
export type FlowApproverRole = z.infer<typeof flowApproverRoleSchema>;

export const FLOW_APPROVAL_STATUSES = [
  "not_required",
  "pending_approval",
  "approved",
  "rejected",
] as const;
export const flowApprovalStatusSchema = z.enum(FLOW_APPROVAL_STATUSES);
export type FlowApprovalStatus = z.infer<typeof flowApprovalStatusSchema>;

// When a card is shown on the home screen: stay until the patient completes its
// CTA (`until_completed`) or be visible for a date window (`date_driven`).
export const FLOW_HOME_VISIBILITIES = [
  "until_completed",
  "date_driven",
] as const;
export const flowHomeVisibilitySchema = z.enum(FLOW_HOME_VISIBILITIES);
export type FlowHomeVisibility = z.infer<typeof flowHomeVisibilitySchema>;

export const FLOW_STATUSES = ["draft", "published", "archived"] as const;
export const flowStatusSchema = z.enum(FLOW_STATUSES);
export type FlowStatus = z.infer<typeof flowStatusSchema>;

// TES-284 (J3) — a flow is either a plain 'flow' (the original) or a 'package':
// several coordinated modules created/scheduled/approved as ONE unit off a
// single trigger, patient-visible only AFTER clinician approval. A package is
// NOT a new table — it's a Flow with kind='package' whose modules are its
// existing FlowItems; the bundle-approval gate lives on the FlowAssignment.
export const FLOW_KINDS = ["flow", "package"] as const;
export const flowKindSchema = z.enum(FLOW_KINDS);
export type FlowKind = z.infer<typeof flowKindSchema>;

// TES-284 — the clinical risk band of a package, snapshotted onto the
// assignment at assign time so the approval queue can rank/triage without
// re-reading the template.
export const FLOW_RISK_LEVELS = ["low", "medium", "high"] as const;
export const flowRiskLevelSchema = z.enum(FLOW_RISK_LEVELS);
export type FlowRiskLevel = z.infer<typeof flowRiskLevelSchema>;

// TES-284 — when a package's modules become patient-visible: only after a
// clinician approves the bundle ('after_approval'), or straight away
// ('immediate', i.e. no bundle gate — behaves like a plain flow).
export const FLOW_PATIENT_VISIBILITIES = [
  "after_approval",
  "immediate",
] as const;
export const flowPatientVisibilitySchema = z.enum(FLOW_PATIENT_VISIBILITIES);
export type FlowPatientVisibility = z.infer<typeof flowPatientVisibilitySchema>;

export const FLOW_TRIGGER_TYPES = ["manual", "event"] as const;
export const flowTriggerTypeSchema = z.enum(FLOW_TRIGGER_TYPES);
export type FlowTriggerType = z.infer<typeof flowTriggerTypeSchema>;

// The typed event catalog (TES-224). A published flow with triggerType='event'
// + triggerEvent=one-of-these is the data-driven trigger registry: when the
// owning service fires the event for a patient, every matching flow assigns.
// Each event is fired from the service that owns the state change (see
// apps/api/src/flows/fire-flow-event.ts for the firing wiring).
//
// `weight_goal_reached` is a legacy alias since TES-998 (see its entry).
// `phase_changed` graduated in TES-280: the daily
// maintenance sweep fires it when a patient crosses into the maintenance
// phase (idempotencyKey `phase_changed:maintenance:<patientId>`).
export const FLOW_EVENTS = [
  "first_consult_completed",
  "lab_results_ready",
  "treatment_activated",
  "cycle_renewed",
  "subscription_active",
  "weight_logged",
  "check_in_submitted",
  "intake_submitted",
  // TES-373: fired by the booking service when a patient books a meeting
  // (idempotencyKey `meeting_booked:<meetingId>`). Doctor appointments only —
  // lab-test bookings happen OUTSIDE the system (the patient books at a lab
  // directly), so no booking signal exists for lab tasks; those resolve on
  // `lab_results_ready` instead.
  "meeting_booked",
  // Behavioral / derived signals, fired by the daily BehavioralTriggerJob
  // (TES-225) when a patient drifts — e.g. no check-in or no weight log within
  // the window. At most once per ISO week per signal (cooldown ledger).
  "missed_check_in",
  "missed_weight_log",
  // TES-751 — "Behöver återkontakt". Emelie 2026-09-17 redefined the signal
  // behind coachens sektion as TWO_CONSECUTIVE_WEEKLY_CHECKINS_MISSED: two
  // COMPLETE cycles ended without an F-04, where a week F-05 replaced counts
  // as present. Deliberately a separate key from `missed_check_in` rather
  // than a retuned threshold — the two say different things (one missed week
  // vs. a patient to reach out to), and a journey wired to the old key must
  // not silently start firing on the new meaning.
  "two_weekly_checkins_missed",
  // TES-280: fired by the maintenance sweep (phase → maintenance).
  //
  // ⚠️ NOT the signal that a stabilisation plan started. This one comes from a
  // NIGHTLY CALENDAR SWEEP with no clinician in the loop — it means "twelve
  // months have passed", not "a doctor decided". Emelie 2026-09-22 (Ö-205):
  // In her words it does not express the same semantics and may not start
  // CP-61; the twelve-month boundary may never change the clinical phase on
  // its own. Use `stabilisation_plan_activated` for content that must wait
  // for the doctor.
  "phase_changed",
  // TES-860 (Ö-205) — the doctor ACTIVATED a weight-stabilisation plan.
  //
  // The event CP-61 starts on. Emelie's three preconditions, all of which
  // hold at the moment this fires:
  //
  //   1. the doctor explicitly chose WEIGHT_STABILISATION (never derived,
  //      never defaulted — a null phase is a state to refuse on),
  //   2. the positive treatment decision is signed and published,
  //   3. the continued plan/dose and prescription status are registered.
  //
  // Fired from BOTH decision surfaces — the post-visit approval and the
  // asynchronous F-07 transfer review — because a patient reaching
  // stabilisation through one route and not the other would give the same
  // clinical state two different journeys.
  //
  // Keyed on the CYCLE that carried the decision, not the treatment: a
  // patient can legitimately leave stabilisation and return, and a
  // treatment-keyed key would swallow the second activation forever.
  "stabilisation_plan_activated",
  // LEGACY ALIAS of `maintenance_requested` since TES-998 (Ö-238).
  //
  // It used to fire on ANY logged weight at or below the goal, in any phase,
  // and CP-60 triggers on it. Ö-238 moved that moment: a weight at the goal
  // now only opens her choice (`goal_zone_candidate_detected`), and CP-60 —
  // whose published copy reads "Du har valt att behålla …", i.e. the state
  // AFTER she chose to keep her weight — starts when she chooses
  // "Jag vill behålla ungefär den vikt jag har nu". So GoalZoneService fires
  // this alongside `maintenance_requested`, keyed on the same confirmation,
  // until CP-60 has been repointed; then it can be dropped. Nothing else
  // fires it any more.
  "weight_goal_reached",
  // TES-998 (Ö-238) — the goal-zone events, names from the canonical CP-60
  // document. All fired by GoalZoneService and keyed on the confirmation row,
  // so a re-fired weight or a double tap creates nothing twice.
  //
  // A weight at or below the registered goal, from any source that writes
  // her weight series, while she is in ACTIVE_TREATMENT and has no active
  // confirmation. Opens the choice; decides nothing.
  "goal_zone_candidate_detected",
  // She settled the choice: kept her weight or set a new goal. What content
  // ends the choice package on.
  "goal_zone_choice_recorded",
  // "Jag vill fundera lite": the full choice closes, the choice itself stays
  // open — "ett lågprioriterat statuskort med CTA:n 'Ta ställning senare'"
  // "ligger kvar". What content completes the choice card on, so the
  // low-priority card behind it shows.
  "goal_zone_choice_deferred",
  // The weight that opened the choice was corrected above the goal before
  // she chose: "valuppgiften tas bort om villkoren inte längre är uppfyllda".
  "goal_zone_candidate_withdrawn",
  // "Jag vill behålla ungefär den vikt jag har nu" — a WISH, carried into
  // the next monthly review. Starts CP-60. Never changes the clinical phase.
  "maintenance_requested",
  // She set a new goal while a keep-wish was open: "Sparat viktmål emitterar"
  // "patient_withdraws_maintenance_request". Ends CP-60.
  "patient_withdraws_maintenance_request",
  // TES-1011 — the doctor answered her keep-wish at the monthly review with
  // continued active treatment (canon: "doctor_decision="
  // "CONTINUE_ACTIVE_TREATMENT → aktiv resa återupptas"). Fired when the
  // review is published, together with the feedback that explains it:
  // "Patientens hemskärm får inte bara växla tillbaka utan förklaring". Keyed
  // on the confirmation, so a republish fires nothing twice. Ends CP-60 once
  // the package lists it in completesAssignmentOn (a content change).
  "maintenance_request_declined",
  // TES-384 — Drive-journey events wired to their existing domain actions so
  // Codex's packages can flip from manual to event triggers. Each fires from
  // exactly one server-side mutation; none are client "viewed" telemetry
  // (deliberately out of the event model — care-relevant facts only).
  //
  // ConsultPaymentService.markPaid — the one-time consult fee flipped to paid
  // (Stripe webhook or the dev mock confirm both land here).
  "meeting_paid",
  // BookingService.cancel — the booked consult was cancelled (idempotencyKey
  // `meeting_cancelled:<meetingId>`). A REBOOK is modelled as cancel + new
  // booking: this event ends the meeting-scoped packages (CP-04) and the new
  // `meeting_booked` re-triggers them.
  "meeting_cancelled",
  // MeetingWorkflowService.documentMeeting — the doctor documented the
  // meeting (decisions/next steps recorded).
  "doctor_next_step_set",
  // TES-739 (CP-05) — the video session reached a VERIFIED end: Daily's
  // `meeting.ended` webhook (BookingService) or the doctor documenting a
  // live meeting (MeetingWorkflowService). Never the cron sweep, never a
  // no_show/cancelled meeting — a passed clock is not a held consult.
  "doctor_visit_session_ended",
  // TES-739 — Drive's canonical name for doctor_next_step_set; fired
  // alongside it with the same meeting-keyed idempotency, so packages can
  // be authored against either name.
  "doctor_visit_documented",
  // LabsService.sendOrderFeedback — the doctor sent lab feedback to the
  // patient (the medical decision; re-sends dedupe on the order key).
  "lab_feedback_approved",
  // FlowChatMessageDraftService.approve — a clinician approved an authored
  // chat draft and the message was delivered to the patient.
  "coach_feedback_approved",
  // FormsService.submitForm(kind=milestone) — fired ALONGSIDE the generic
  // check_in_submitted (kept for existing completesOn) so monthly-milestone
  // packages can trigger specifically.
  "milestone_submitted",
  // PrescriptionCycleService renewal approve — the doctor approved the
  // monthly review (fires alongside cycle_renewed, which tracks the CYCLE
  // fact; this one tracks the REVIEW decision).
  "monthly_review_approved",
  // TES-898 — PrescriptionCycleService.publishMonthlyReview: the doctor's
  // PATIENT-FACING monthly feedback is signed and published.
  //
  // Distinct from `monthly_review_approved` above, and CP-33 needs this one.
  // That event fires when a RENEWAL is approved, which is a different moment
  // and does not imply any patient-facing text exists: a doctor can approve
  // the renewal and not publish, and can publish without a renewal in the
  // same breath. CP-33 pointed at the approval and therefore could offer
  // "läs läkarens återkoppling" for feedback that had not been written.
  //
  // Canon (CP-33):
  // "Om återkopplingen ännu inte har publicerats får CP-33 inte visas", and
  // "En lyckad publicering skapar monthly_medical_review_published och aktiverar automatiskt CP-33".
  //
  // Keyed on the cycle, so a republish with edited text does not assign the
  // package a second time — the patient already has it, and the notification
  // is what tells her the text changed.
  "monthly_medical_review_published",
  // WeightService measurements — the patient submitted body measurements
  // (waist/hip).
  "body_measurement_submitted",
  // TES-394 — the patient row was created (first successful sign-in, BankID
  // or dev-auth). Fired once per patient (idempotencyKey `signed_up:<id>`);
  // the app-first CP-00A package event-triggers on this so the F-01 quiz
  // card appears on Hem without manual assignment.
  "signed_up",
  // TES-389 follow-up (F-01 app-first) — the patient completed F-01, the short
  // suitability/indication quiz (the web BMI quiz's question set, in-app).
  // Fired by F01Service on an in-app submission AND by LeadsService when a
  // pre-signup web-quiz lead links to the patient (the quiz was already done
  // on the web — CP-00A/B must not re-ask). NOT the medical intake (F-02 =
  // the health declaration → intake_submitted).
  "f01_submitted",
  // TES-522 §8 — the patient actively chose "Boka vårdmöte" from an early
  // stop result. CP-01 may trigger on THIS, never on the stop signal itself.
  "f01_booking_intent",
  // QA-baseline 2026-08-19 (CP-09 A/B/C) — the treatment decision's OUTCOME as
  // three distinct events, so the result-view packages can event-trigger on
  // the right variant. Fired from the meeting-workflow documentation branch
  // (labRequired/treatmentProceed); the post-labs decision point wires in when
  // that flow crystallizes. All three are simulate-able via test mode.
  "treatment_decision_approved",
  "treatment_decision_rejected",
  "treatment_decision_completion_required",
  // TES-537 (Ö-4, A-02) — the doctor's "Nästa steg efter vårdmötet", one
  // event per CP-06 variant so the right package triggers: existing results
  // to upload (+ BP/pulse), a new blood test (+ BP/pulse), or results already
  // verified in a trusted source (BP/pulse only).
  // "Patienten kan inte gå vidare" fires none of these — only
  // treatment_decision_rejected.
  "next_step_upload_existing_labs",
  "next_step_new_blood_test",
  "next_step_labs_verified",
  // The patient reported taking her ACTUAL first dose.
  // DT-01 hangs off this and nothing else: "starting treatment" in our data
  // means a decision was made, which is not the same day a patient injects
  // for the first time.
  //
  // TES-841 — what withholds it is the CLINICAL PHASE, not the patient group.
  // This comment used to say a prescription transfer never fires it, which
  // stopped being true when the doctor gained an explicit phase choice: a
  // transfer patient the doctor puts on active treatment does have a first
  // Ella dose to report. `canReportFirstDose` reads
  // `Treatment.clinicalPhase`, and only `weight_stabilisation` is excluded —
  // there the treatment is already running and CP-12 is skipped entirely.
  // TES-1436 (Emelie 2026-10-19) — except for a prescription transfer, who
  // registers her first dose with Ella (CP-12's transfer variant) in either
  // phase; for her the event anchors dose days and check-ins and triggers no
  // flow (apps/api cycle/first-dose-in-ella.ts).
  "first_dose_reported",
  // TES-997 (Ö-259) / TES-903 — CP-12 variant A: a new prescription was
  // issued in Webdoc and published, so the patient has a medicine to
  // collect. Fired ONLY by PrescriptionCycleService when the doctor chose
  // "Nytt recept är utfärdat i Webdoc" (keyed on the cycle that carries it),
  // never by autosave, a meeting ending, a journal or feedback. The cycle's
  // `medicationPickupRequired` column holds the same fact for readers.
  "medication_pickup_required",
  // TES-1160 — her answer to a planned weekly dose day's task
  // ("Dags för veckans dos"): payload { cycleId, plannedOn, outcome } with
  // outcome taken | not_taken. Fired only by the patient's POST
  // (patient-api/v1/treatment/dose), once per planned dose day
  // (`dose_reported:<patientId>:<plannedOn>`): the first answer stands. The
  // first dose is NOT this event — that stays `first_dose_reported`.
  "dose_reported",
  // TES-903 — CP-12 variant A's check-off: the patient's own
  // "Jag har hämtat ut läkemedlet". Fired only by the patient's POST
  // (patient-api/v1/treatment/medication-pickup), once per cycle that carries
  // `medicationPickupRequired = true`, with status PATIENT_REPORTED and
  // source CP-12 in the payload. A self-report: it verifies no pharmacy
  // dispensing, never starts DT-01 on its own and never completes CP-12 —
  // only `first_dose_reported` does that.
  "medication_pickup_reported",
  // Ö-94 / TES-537 — the patient recorded blood pressure
  // AND resting pulse in one MeasurementSession under "Dina värden". Every
  // CP-06 variant carries that task; this is what completes it. Fired by
  // MeasurementService once per session that holds both.
  "vitals_recorded",
  // Ö-128 — EVERY mandatory task in the patient's assigned
  // CP-06 track is done. The point of the event is that it is track-agnostic:
  // `lab_results_ready` only ever covers CP-06B, so a patient on the
  // upload-existing-labs or labs-verified track would never reach a
  // "waiting for the doctor" state at all.
  //
  // Derived, not owned by one mutation: medicalEvidenceChecklist() computes
  // the task set from the doctor's documented next step and each task's state
  // from what the patient has done SINCE that consult. Three mutations can be
  // the last one — an evidence upload, lab results landing, and a BP+pulse
  // session — so all three re-check and this fires on whichever completes the
  // set. Idempotency is the DomainEvent key (one per case, keyed on the
  // consult's documentedAt), not a new column.
  //
  // It does NOT close the case: only the published treatment decision (CP-09)
  // does that (TES-590).
  "medical_evidence_completed",
  // TES-720 — the CP-06 document's name for the same moment:
  // "patientens uppgifter i Ella är klara" (level 1 of three — not received,
  // not reviewed). Fired by the same re-check as `medical_evidence_completed`,
  // which lives on as the cp_08 alias until the packages have been repointed.
  // Since 2026-09-16 it also counts the payment row in Variant B as required.
  "patient_evidence_tasks_completed",
  // TES-729 — the prescription-transfer events, names locked by the
  // Implementation Contract ("MVP-TILLÄGG — RECEPTÖVERTAG") + Flow Engine.
  // Fired by FormsService when F-02 + F-07 open the review (`_submitted`), when
  // a requested completion is submitted (`_resubmitted`), and by
  // TransferReviewService per doctor outcome. Keyed per review row, so a
  // replayed webhook or a double click fires nothing twice.
  "prescription_transfer_submitted",
  "prescription_transfer_resubmitted",
  "prescription_transfer_more_information_requested",
  "prescription_transfer_labs_requested",
  "prescription_transfer_visit_required",
  "prescription_transfer_approved",
  "prescription_transfer_cannot_be_taken_over",
] as const;
// The CP numbering IS the home-context order: a lower
// CP number is the earlier, ongoing ACTION and owns the home-screen context
// while a later number rides along as status (CP-03 > CP-04 while both are
// active). Keys carry descriptive suffixes (cp_03_complete_health_declaration)
// so all matching in the system is prefix-based; non-CP keys return null and
// sort after every CP. Shared by the theme-overlay resolution and the
// bulk-import home-context validation — one constant, two consumers.
export function cpNumberOf(flowKey: string): number | null {
  const m = /^cp_(\d+)/.exec(flowKey);
  return m ? Number(m[1]) : null;
}

export const flowEventSchema = z.enum(FLOW_EVENTS);
export type FlowEvent = z.infer<typeof flowEventSchema>;

export const FLOW_ASSIGNMENT_SOURCES = ["trigger", "manual"] as const;
export const flowAssignmentSourceSchema = z.enum(FLOW_ASSIGNMENT_SOURCES);
export type FlowAssignmentSource = z.infer<typeof flowAssignmentSourceSchema>;

export const FLOW_ASSIGNMENT_STATUSES = [
  "active",
  // TES-288: temporarily suspended by a clinician — items hidden from the
  // patient feed, pending notifications cancelled, event auto-completion
  // blocked. Resumable (unlike cancelled).
  "paused",
  "completed",
  "cancelled",
] as const;
export const flowAssignmentStatusSchema = z.enum(FLOW_ASSIGNMENT_STATUSES);
export type FlowAssignmentStatus = z.infer<typeof flowAssignmentStatusSchema>;

export const FLOW_ASSIGNMENT_ITEM_STATUSES = [
  "scheduled",
  "removed",
  "sent",
  "completed",
] as const;
export const flowAssignmentItemStatusSchema = z.enum(
  FLOW_ASSIGNMENT_ITEM_STATUSES,
);
export type FlowAssignmentItemStatus = z.infer<
  typeof flowAssignmentItemStatusSchema
>;

// TES-287 (task-state lifecycle) — the PATIENT-facing progress of a completable
// item, orthogonal to the delivery `status` above. `status` tracks the
// materialized row's plumbing (scheduled→sent→completed / removed); this tracks
// what the patient has DONE with the task:
//
//   `ny`        — materialized, patient hasn't touched it
//   `påbörjad`  — patient saved a partial form draft (started, not submitted)
//   `inskickad` — patient submitted the task/form (awaiting clinician)
//   `bokad`     — patient booked the meeting a booking-task asks for (alt submit)
//   `granskad`  — a clinician reviewed the submission (resolved)
//
// Only completable module types (COMPLETABLE_MODULE_TYPES) carry a meaningful
// task state; consumers gate on `moduleType` before reading it. TES-286 maps it:
// `ny`/`påbörjad` → surface as a patient-action candidate;
// `inskickad`/`bokad` → drop from the action tiers (awaiting clinician);
// `granskad` → resolved / tier-3 feedback candidate.
export const PATIENT_TASK_STATES = [
  "ny",
  "påbörjad",
  "inskickad",
  "bokad",
  "granskad",
] as const;
export const patientTaskStateSchema = z.enum(PATIENT_TASK_STATES);
export type PatientTaskState = z.infer<typeof patientTaskStateSchema>;

// Allowed forward transitions. `ny` is the birth state; `granskad` is terminal.
// `inskickad`/`bokad` are the two "submitted, awaiting clinician" terminals a
// patient can reach; both then advance to `granskad` on clinician review.
export const PATIENT_TASK_STATE_TRANSITIONS: Record<
  PatientTaskState,
  readonly PatientTaskState[]
> = {
  ny: ["påbörjad", "inskickad", "bokad"],
  påbörjad: ["inskickad", "bokad"],
  inskickad: ["granskad"],
  bokad: ["granskad"],
  granskad: [],
};

export function canTransitionTaskState(
  from: PatientTaskState,
  to: PatientTaskState,
): boolean {
  return PATIENT_TASK_STATE_TRANSITIONS[from].includes(to);
}

// TES-287 — the two "submitted" task states (patient done, clinician not yet).
export const SUBMITTED_TASK_STATES: readonly PatientTaskState[] = [
  "inskickad",
  "bokad",
];

// "HH:MM" 24-hour local time-of-day for push/email surfaces.
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "timeOfDay must be HH:MM (24h)");

// A deep-link / in-app route a CTA navigates to, e.g. "carebyella://blood-test"
// or an in-app route name. Kept a free string; the mobile route table validates.
// TES-362: authored CTA paths must be resolvable — a known carebyella://
// deep link or an http(s) URL — so imports fail loud instead of shipping
// dead buttons (the mobile resolver silently no-ops unknown paths).
export const ctaPathSchema = resolvableCtaPathSchema;

// TES-227 — a flow step's content-module TYPE (what it IS), orthogonal to its
// `surfaces` (the channels it's delivered/reminded on). `content` is the plain
// card (the original behaviour, backward compatible); the others are typed
// modules surfaced in their own in-app section. `chat_message` authoring +
// approval lands in TES-233.
export const FLOW_MODULE_TYPES = [
  "content",
  "task",
  "check_in",
  "reflection",
  "resource",
  "medication",
  "assessment",
  "article",
  "chat_message",
] as const;
export const flowModuleTypeSchema = z.enum(FLOW_MODULE_TYPES);
export type FlowModuleType = z.infer<typeof flowModuleTypeSchema>;

// Module types whose item can be completed (manually or by an event). Used to
// validate `completesOn` + drive the task section / completion engine (TES-229).
export const COMPLETABLE_MODULE_TYPES: readonly FlowModuleType[] = [
  "task",
  "check_in",
  "reflection",
  "assessment",
  "medication",
];

// Article reference payload — a flow step that surfaces a published Article.
export const flowArticleContentSchema = z.object({
  articleKey: z.string().min(1),
});
export type FlowArticleContent = z.infer<typeof flowArticleContentSchema>;

// TES-233 — chat-message step payload: the authored template the flow renders
// into a DRAFT message a clinician approves before it's sent. `attribution` is
// the persona the patient sees; `generationStrategy` is 'template' now, 'ai'
// later (same approval pipeline).
export const FLOW_CHAT_ATTRIBUTIONS = ["coach", "doctor"] as const;
export const flowChatAttributionSchema = z.enum(FLOW_CHAT_ATTRIBUTIONS);
export type FlowChatAttribution = z.infer<typeof flowChatAttributionSchema>;

export const FLOW_CHAT_GENERATION_STRATEGIES = ["template", "ai"] as const;
export const flowChatGenerationStrategySchema = z.enum(
  FLOW_CHAT_GENERATION_STRATEGIES,
);
export type FlowChatGenerationStrategy = z.infer<
  typeof flowChatGenerationStrategySchema
>;

export const flowChatMessageContentSchema = z.object({
  attribution: flowChatAttributionSchema,
  body: localizedTextSchema,
  generationStrategy: flowChatGenerationStrategySchema.default("template"),
});
export type FlowChatMessageContent = z.infer<
  typeof flowChatMessageContentSchema
>;

// ---------------------------------------------------------------------------
// Per-surface content — localized {sv,en}
// ---------------------------------------------------------------------------

// TES-361 — authored copy requires non-empty `sv` (en falls back to sv at
// render time since TES-362, so empty sv = blank surface, fail loud instead).
export const flowHomeCardContentSchema = z.object({
  title: svRequiredLocalizedTextSchema,
  body: svRequiredLocalizedTextSchema,
  ctaLabel: svRequiredLocalizedTextSchema.optional(),
  ctaPath: ctaPathSchema.optional(),
});
export type FlowHomeCardContent = z.infer<typeof flowHomeCardContentSchema>;

export const flowPushContentSchema = z.object({
  title: svRequiredLocalizedTextSchema,
  body: svRequiredLocalizedTextSchema,
  ctaPath: ctaPathSchema.optional(),
});
export type FlowPushContent = z.infer<typeof flowPushContentSchema>;

export const flowEmailContentSchema = z.object({
  subject: svRequiredLocalizedTextSchema,
  heading: svRequiredLocalizedTextSchema,
  body: svRequiredLocalizedTextSchema,
  ctaLabel: svRequiredLocalizedTextSchema.optional(),
  ctaPath: ctaPathSchema.optional(),
});
export type FlowEmailContent = z.infer<typeof flowEmailContentSchema>;

// One item's content bundle. Each key is present iff the matching surface is in
// `surfaces` (enforced by `flowItemSchema` below).
// TES-872 — when an item is shown, relative to whether the
// patient's treatment plan is LIVE yet.
//
// CP-61 needs two versions of the same row, and canon gives both:
//
//   awaiting_activation — "Din fortsatta plan förbereds", no CTA
//   active              — "Se min fortsatta plan", with the CTA
//
// They are the same plan in two states, not two competing strings. A transfer
// patient reaches the first for real: `recordApproval` writes the verified
// order onto the cycle and fires `stabilisation_plan_activated`, but leaves
// the cycle `pending_assessment` — the subscription activates it (S5). The
// view already names that step `awaiting_activation`.
//
// Authored items carry fixed copy and cannot branch, and the package cannot
// see cycle state. So the AUTHOR writes both rows and the SERVER shows one.
// Absent means "always", so every existing item is unaffected.
export const FLOW_TREATMENT_GATES = ["awaiting_activation", "active"] as const;
export const flowTreatmentGateSchema = z.enum(FLOW_TREATMENT_GATES);
export type FlowTreatmentGate = z.infer<typeof flowTreatmentGateSchema>;

// TES-898 — a SECOND gate axis, on the same principle as the treatment gate
// above: the author writes the row, the server decides whether it shows.
//
// CP-33 is one package with two parts, and only the first is unconditional.
// Canon:
// "Hämta ut ditt läkemedel … visas endast när ansvarig läkare uttryckligen har registrerat att receptet faktiskt är utfärdat. Ett utkast, en planerad förnyelse eller enbart publicerad månadsåterkoppling får inte skapa uppgiften."
//
// `issued` is that registration, and Emelie named the fields (2026-09-23):
// the current cycle carries BOTH `prescribedAt` and `prescribedByDoctorId`.
// Both, not either — the timestamp alone does not say a named doctor stood
// behind it, and it is the doctor's act that canon calls explicit. She also
// ruled that `webdocPrescriptionRef` is stored when present but must not be
// the condition, so the gate never waits on an integration.
//
// Deliberately NOT a second value on FLOW_TREATMENT_GATES: that axis is about
// whether the plan is live, this one about whether a prescription exists, and
// a package may need to gate on both. Absent means "always", so every item
// authored before this is unaffected.
export const FLOW_PRESCRIPTION_GATES = ["issued"] as const;
export const flowPrescriptionGateSchema = z.enum(FLOW_PRESCRIPTION_GATES);
export type FlowPrescriptionGate = z.infer<typeof flowPrescriptionGateSchema>;

export const flowItemContentSchema = z.object({
  home: flowHomeCardContentSchema.optional(),
  // TES-872 — see FLOW_TREATMENT_GATES. Optional: no gate = always shown.
  treatmentGate: flowTreatmentGateSchema.optional(),
  // TES-898 — see FLOW_PRESCRIPTION_GATES. Optional, and independent of the
  // treatment gate: an item may carry both, and passes only if both match.
  prescriptionGate: flowPrescriptionGateSchema.optional(),
  push: flowPushContentSchema.optional(),
  email: flowEmailContentSchema.optional(),
  // TES-227 — present for `article`-type steps (references a published Article).
  article: flowArticleContentSchema.optional(),
  // TES-233 — present for `chat_message`-type steps (the draft template).
  chatMessage: flowChatMessageContentSchema.optional(),
});
export type FlowItemContent = z.infer<typeof flowItemContentSchema>;

// ---------------------------------------------------------------------------
// Flow template (draft/published, versioned)
// ---------------------------------------------------------------------------

export const flowItemSchema = z
  .object({
    // Present on persisted items; absent on new items in a draft save.
    id: z.string().optional(),
    dayOffset: z.number().int().min(0),
    timeOfDay: timeOfDaySchema.default("09:00"),
    order: z.number().int().min(0),
    // TES-227 — what this step is. Defaults to `content` (the original plain
    // card) so existing flows are unchanged.
    moduleType: flowModuleTypeSchema.default("content"),
    // TES-233 — a chat_message step has no card surface (it's delivered to the
    // patient's chat on approval), so surfaces may be empty for it; every other
    // type still needs ≥1 surface (enforced in superRefine).
    surfaces: z.array(flowSurfaceSchema),
    homeVisibility: flowHomeVisibilitySchema.optional(),
    // TES-227/229 — the event that auto-completes this item (completable types
    // only), e.g. a "log weight" task with completesOn=`weight_logged`.
    completesOn: flowEventSchema.optional(),
    // TES-285 (J4) per-module fields — all defaulted so existing flows are
    // unchanged. Consumed by TES-286 (priority engine) + TES-284 (packages).
    // The authorable 1-100 priority TES-286 ranks a home_now candidate by.
    priority: z.number().int().min(1).max(100).default(50),
    // end_at as a relative window (dayOffset = start, per J14): the item's
    // visibility/candidacy window ends `durationDays` after its dueOn.
    durationDays: z.number().int().positive().nullable().optional(),
    // TES-286: a required candidate ranks above non-required in its tier.
    required: z.boolean().default(false),
    // TES-286: surfaces prominently as a blocking task (does NOT hard-block
    // journeyState.advance() — MVP soft-gate).
    blocksNextState: z.boolean().default(false),
    // The "calm status / next step" item, shown only when no non-fallback
    // candidate exists (TES-311 fallback rule).
    fallback: z.boolean().default(false),
    // Generalized approval (TES-284 packages hoist this to a bundle gate):
    // does a materialized instance need clinician approval before it's
    // patient-visible.
    approvalRequired: z.boolean().default(false),
    approverRole: flowApproverRoleSchema.nullable().optional(),
    content: flowItemContentSchema,
  })
  .superRefine((item, ctx) => {
    const has = (s: FlowSurface) => item.surfaces.includes(s);
    // Every step except chat_message needs at least one delivery channel.
    if (item.moduleType !== "chat_message" && item.surfaces.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["surfaces"],
        message: "at least one surface required",
      });
    }
    // TES-227 — an article step must carry its article reference.
    if (item.moduleType === "article" && !item.content.article) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content", "article"],
        message: "article content (articleKey) required for an 'article' step",
      });
    }
    // TES-233 — a chat_message step must carry its message template.
    if (item.moduleType === "chat_message" && !item.content.chatMessage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content", "chatMessage"],
        message: "chatMessage content required for a 'chat_message' step",
      });
    }
    // completesOn only makes sense for a completable module type.
    if (
      item.completesOn &&
      !COMPLETABLE_MODULE_TYPES.includes(item.moduleType)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completesOn"],
        message: `completesOn is only valid for a completable step (${COMPLETABLE_MODULE_TYPES.join(", ")})`,
      });
    }
    // TES-351 — a completable step is acted on in "Vad händer just nu" (§2),
    // the canonical home action surface since the TES-311 rework retired the
    // home_card feed. Without home_now it materializes but never reaches the
    // patient (home_card renders nowhere on mobile anymore).
    if (
      COMPLETABLE_MODULE_TYPES.includes(item.moduleType) &&
      !has("home_now")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["surfaces"],
        message: "a completable step must include the 'home_now' surface",
      });
    }
    // homeVisibility required iff home_card is a surface (legacy feed semantics;
    // home_now visibility is governed by durationDays + patientTaskState).
    if (has("home_card") && !item.homeVisibility) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["homeVisibility"],
        message: "homeVisibility is required when 'home_card' is a surface",
      });
    }
    if (!has("home_card") && item.homeVisibility) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["homeVisibility"],
        message: "homeVisibility is only valid when 'home_card' is a surface",
      });
    }
    // Each declared surface must carry its content. home_now shares the `home`
    // content shape with the legacy home_card — without this, a home_now item
    // missing `content.home` validated fine and was silently skipped at read
    // time (home-now.service's defensive `if (!home) continue`).
    if ((has("home_card") || has("home_now")) && !item.content.home) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content", "home"],
        message: "home content required for the 'home_card'/'home_now' surface",
      });
    }
    if (has("push") && !item.content.push) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content", "push"],
        message: "push content required for the 'push' surface",
      });
    }
    if (has("email") && !item.content.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content", "email"],
        message: "email content required for the 'email' surface",
      });
    }
    // TES-285 — a fallback item is the calm "nothing needs attention" state;
    // it can't itself be a required/blocking task.
    if (item.fallback && (item.required || item.blocksNextState)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fallback"],
        message: "a fallback item cannot be required or block the next state",
      });
    }
    // TES-285 — a non-chat step that needs approval must name the role that
    // approves it (chat_message rides the TES-233 attribution instead).
    if (
      item.approvalRequired &&
      item.moduleType !== "chat_message" &&
      !item.approverRole
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["approverRole"],
        message: "approverRole is required when approvalRequired is set",
      });
    }
  });
export type FlowItem = z.infer<typeof flowItemSchema>;

export const flowTriggerSchema = z
  .object({
    type: flowTriggerTypeSchema,
    // Event key (when type='event') — one of the typed FLOW_EVENTS catalog.
    event: flowEventSchema.optional(),
  })
  .superRefine((t, ctx) => {
    if (t.type === "event" && !t.event) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["event"],
        message: "event is required when trigger type is 'event'",
      });
    }
  });
export type FlowTrigger = z.infer<typeof flowTriggerSchema>;

// TES-370 — opaque per-flow/package author metadata (the "Ögonblick" fields
// Codex's Drive model carries: theme_behavior, primary_patient_need,
// primary_message, active_journey_context, patient_visible_return,
// context_layers_used/intent, women_health_context_used,
// behavior_wellbeing_topic, min_resa_publishable, min_resa_entry_type,
// measurement_hypothesis_tag, …). A LOSSLESS PASSTHROUGH carrier: never
// interpreted by the platform — stored on the Flow, snapshotted onto the
// FlowAssignment at assign time (in-flight immutability, like flow items) and
// shown read-only in the admin editor. Kept an opaque record so new
// authoring-side fields need no contract change; size-capped so an import
// can't stuff megabytes into a Json column.
export const AUTHOR_METADATA_MAX_CHARS = 8_192;
export const authorMetadataSchema = z
  .record(z.string(), z.unknown())
  .superRefine((value, ctx) => {
    if (JSON.stringify(value).length > AUTHOR_METADATA_MAX_CHARS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `authorMetadata must serialize to at most ${AUTHOR_METADATA_MAX_CHARS} characters`,
      });
    }
  });
export type AuthorMetadata = z.infer<typeof authorMetadataSchema>;

// TES-372 — how a package interacts with the weekly theme (§1+§5 veckotema)
// while it is ACTIVE and bundle-approved. The five values are the Drive
// ContentPackage model's canonical `theme_behavior` set (Drive is truth):
//   preserve_weekly        — never touches the theme (the historical default)
//   contextualize_weekly   — base theme stays; the package's `theme` label is
//                            appended as a context line on "För dig idag"
//   set_journey_context    — the package's own theme copy replaces the base
//   start_new_weekly_theme — ditto at runtime; authoring-side it marks a new
//                            weekly arc rather than a contextual override
//   override_for_safety    — a medical-safety pause: outranks everything
//                            (the spec's "only medical safety pauses the theme")
//   override_for_you_only  — QA-baseline 2026-08-19: the CP takes over ONLY
//                            "För dig idag" (§5); DT keeps "Tema" + "Hälsning".
//                            Composes with a simultaneous replace overlay
//                            (CP-06/CP-19/CP-30 over an onboarding CP);
//                            suppressed while a safety pause is active.
// Runtime collapses set/start to the same replacement behavior on purpose —
// the distinction is authorial. Revisit against Drive if it drifts.
export const THEME_BEHAVIORS = [
  "preserve_weekly",
  "contextualize_weekly",
  "set_journey_context",
  "start_new_weekly_theme",
  "override_for_safety",
  "override_for_you_only",
] as const;
export const flowThemeBehaviorSchema = z.enum(THEME_BEHAVIORS);
export type FlowThemeBehavior = z.infer<typeof flowThemeBehaviorSchema>;

// The package's own theme copy — the four §1+§5 slots, bilingual like the
// authored WeeklyTheme rows (same render surface, same language contract).
// Required for set_journey_context/start_new_weekly_theme (they replace the
// base copy), optional for override_for_safety (a built-in safety-pause copy
// exists as fallback), ignored for the other behaviors.
// title/body are optional at the schema level because override_for_you_only
// brings ONLY the §5 slots; refinePackageHeader enforces per-behavior
// completeness (set/start need all four, for_you_only forbids title/body).
export const packageThemeSchema = z.object({
  title: localizedTextSchema.optional(),
  body: localizedTextSchema.optional(),
  // TES-873 — the §5 pair is optional for the same reason title/body are: a
  // package may bring one half of this object and not the other. A
  // `preserve_weekly` package that only names its question section (below)
  // renders no §5 at all, and inventing copy for a surface it never reaches
  // is what CP-60/61 canon forbids: "Ingen redaktionell För dig idag-yta".
  // TES-887 — that holds for a §1-replacing package too, so the pair is
  // optional for every behavior; refinePackageHeader only insists it is a
  // PAIR, and an absent pair means §5 is not rendered at all.
  forYouTitle: localizedTextSchema.optional(),
  forYouBody: localizedTextSchema.optional(),
  // TES-873 — the heading above the TES-866 `home_questions` section. Its own
  // field because canon gives a package BOTH, as different strings. CP-60's
  // theme is "Din nästa fas" while its section is headed
  // "Vanliga frågor inför nästa fas". Reading the heading off `title` would
  // mean a package can never take over the theme without also renaming its
  // question section.
  questionsTitle: localizedTextSchema.optional(),
  // v15 stop-condition fix: the package theme can steer the §5 "Läs mer"
  // link. Optional — absent means the overlayed "För dig idag" has no CTA
  // (the pre-v15 behaviour).
  forYouArticleKey: z.string().min(1).optional(),
  // Handoff 2026-08-24: a curated §5 CTA label (e.g.
  // "Läs om första steget"). Optional — without it the client renders its
  // generic "Läs mer" label. Meaningful only together with forYouArticleKey.
  forYouCtaLabel: localizedTextSchema.optional(),
});
export type PackageTheme = z.infer<typeof packageThemeSchema>;

// TES-284 — the package header fields. All optional/defaulted, only meaningful
// when kind='package' (a plain flow leaves them at their defaults). Composed
// into flowContentSchema + the authoring bodies below, and validated by the
// shared refinement `refinePackageHeader`.
const packageHeaderShape = {
  // 'flow' (default) | 'package'.
  kind: flowKindSchema.default("flow"),
  // Free-text theme label for the package (e.g. "Blodprovspaket").
  theme: z.string().min(1).optional(),
  riskLevel: flowRiskLevelSchema.optional(),
  // When set, the whole bundle materializes DORMANT and needs a clinician to
  // approve it before any module reaches the patient (notifications + chat
  // drafts are deferred to approval).
  packageApprovalRequired: z.boolean().default(false),
  // Which clinician role must approve the bundle.
  requiredRole: flowApproverRoleSchema.optional(),
  patientVisibility: flowPatientVisibilitySchema.optional(),
  // Days after approval before the package auto-expires (its remaining items
  // stop showing + get swept). Null/absent = never expires.
  expiresAfterDays: z.number().int().positive().nullable().optional(),
  // TES-372 — weekly-theme interaction while the package is active + approved.
  themeBehavior: flowThemeBehaviorSchema.default("preserve_weekly"),
  // TES-372 — the package's own theme copy (see packageThemeSchema).
  packageTheme: packageThemeSchema.nullable().optional(),
  // QA-baseline 2026-08-19 — medically unsigned content: assignable ONLY to
  // isTest patients, enforced server-side at the assignment chokepoint.
  // Pure metadata; never serialized into patient DTOs or patient copy.
  testOnly: z.boolean().default(false),
  // Package-level completion: the assignment completes
  // when ANY of these events fires for the patient, for packages whose
  // purpose is fulfilled by an event they own no item for (CP-04 ends on
  // first_consult_completed/meeting_cancelled). Redundant when a required
  // item's completesOn covers it (CP-03) — the item path stays primary.
  completesAssignmentOn: z.array(flowEventSchema).default([]),
} as const;

// A package that gates itself behind approval must name the role that approves
// it. Shared by flowContentSchema + the authoring bodies.
function refinePackageHeader(
  data: {
    kind: FlowKind;
    packageApprovalRequired: boolean;
    requiredRole?: unknown;
    patientVisibility?: unknown;
    theme?: string;
    themeBehavior: FlowThemeBehavior;
    packageTheme?: unknown;
  },
  ctx: z.RefinementCtx,
): void {
  // TES-372 — theme-behavior authoring rules:
  //   * a non-default behavior is a PACKAGE feature (a plain flow never
  //     touches the theme),
  //   * set/start REPLACE the theme copy, so they must bring it,
  //   * contextualize appends the package's `theme` label, so it needs one.
  // override_for_safety needs neither — a built-in safety-pause copy exists.
  if (data.themeBehavior !== "preserve_weekly" && data.kind !== "package") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["themeBehavior"],
      message: "themeBehavior other than preserve_weekly requires kind=package",
    });
  }
  if (
    data.themeBehavior === "set_journey_context" ||
    data.themeBehavior === "start_new_weekly_theme"
  ) {
    const theme = data.packageTheme as PackageTheme | null | undefined;
    if (!theme || !theme.title || !theme.body) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["packageTheme"],
        message: `packageTheme with title/body is required for themeBehavior=${data.themeBehavior}`,
      });
    }
    // TES-887 — the §5 pair used to be required here too, and that made the
    // shape CP-60/61 canon asks for unauthorable: a package that replaces §1
    // and deliberately renders NO §5.
    //
    // Emelie, CP-60:
    // "Ytan är situationsstyrd information och får inte märkas 'För dig idag'."
    // CP-61: "CP-61 visar ingen redaktionell yta märkt För dig idag." The app's
    // §5 heading comes from its own bundle
    // (HomeScreen: `t('home.sections.forYou')`), so until a build can take
    // that label from the server, the only way to obey canon is to omit §5 —
    // and the old rule forced us to invent copy for it instead.
    //
    // Still a PAIR, though. One half alone renders a titleless or bodiless
    // card, which is the authoring slip the original rule existed to catch.
    // Absent pair ⇒ packageThemeToVeckotema returns forYouToday: null ⇒ the
    // app hides the section, the same path CP-14's pause already uses.
    if (theme && !theme.forYouTitle !== !theme.forYouBody) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["packageTheme"],
        message:
          "forYouTitle and forYouBody are a pair — bring both to render §5, or neither to omit it",
      });
    }
  }
  if (data.themeBehavior === "override_for_you_only") {
    const theme = data.packageTheme as PackageTheme | null | undefined;
    if (!theme || !theme.forYouTitle || !theme.forYouBody) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["packageTheme"],
        message:
          "packageTheme (forYouTitle/forYouBody) is required for themeBehavior=override_for_you_only",
      });
    } else if (theme.title || theme.body) {
      // §1 copy on a §5-only override signals an authoring misunderstanding —
      // it would never render. Fail loud instead of silently dropping it.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["packageTheme"],
        message:
          "override_for_you_only must not carry title/body — only forYouTitle/forYouBody render",
      });
    }
  }
  if (data.themeBehavior === "contextualize_weekly" && !data.theme) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["theme"],
      message:
        "a theme label is required for themeBehavior=contextualize_weekly",
    });
  }
  if (
    data.kind === "package" &&
    data.packageApprovalRequired &&
    !data.requiredRole
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["requiredRole"],
      message:
        "requiredRole is required for a package with packageApprovalRequired",
    });
  }
  // A package that gates itself behind approval is DORMANT until approved, so
  // 'immediate' visibility contradicts it. Reject the contradiction at
  // authoring (M2) — otherwise patientVisibility is decorative (the dormancy
  // gate is packageApprovalRequired; patientVisibility was never read).
  if (data.packageApprovalRequired && data.patientVisibility === "immediate") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["patientVisibility"],
      message:
        "a package requiring approval cannot be patient-visible immediately",
    });
  }
}

// The editable flow definition — one version's worth of header + items.
export const flowContentSchema = z
  .object({
    key: z.string().min(1),
    version: z.number().int().positive(),
    name: z.string().min(1),
    description: z.string().optional(),
    trigger: flowTriggerSchema,
    ...packageHeaderShape,
    // TES-370 — opaque author metadata, carried losslessly (see schema above).
    authorMetadata: authorMetadataSchema.optional(),
    items: z.array(flowItemSchema),
  })
  .superRefine(refinePackageHeader);
export type FlowContent = z.infer<typeof flowContentSchema>;

// The admin editor view: live published version + the single in-flight draft.
// Either may be null (a brand-new flow has only a draft; an unedited published
// flow has only a published).
export const flowEditorViewSchema = z.object({
  published: flowContentSchema.nullable(),
  draft: flowContentSchema.nullable(),
});
export type FlowEditorView = z.infer<typeof flowEditorViewSchema>;

// Row in the admin flow list (GET /flows).
export const flowSummarySchema = z.object({
  key: z.string(),
  name: z.string(),
  status: flowStatusSchema,
  version: z.number().int(),
  triggerType: flowTriggerTypeSchema,
  triggerEvent: z.string().nullable().optional(),
  // TES-284 — distinguishes a plain flow from a package in the admin list.
  kind: flowKindSchema.default("flow"),
  // QA-baseline 2026-08-19 — surfaced so the admin list can badge TEST_ONLY.
  testOnly: z.boolean().default(false),
  itemCount: z.number().int(),
  updatedAt: z.string(),
});
export type FlowSummary = z.infer<typeof flowSummarySchema>;

// Base object (no refinement) so createFlowRequestSchema can still `.extend`
// it — a superRefine turns a schema into a ZodEffects, which has no `.extend`.
// TES-284 — the package header fields ride the authoring body too.
const upsertFlowDraftObject = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  trigger: flowTriggerSchema,
  ...packageHeaderShape,
  // TES-370 — opaque author metadata, carried losslessly (see schema above).
  authorMetadata: authorMetadataSchema.optional(),
  items: z.array(flowItemSchema),
});

// Body to create/update a draft flow (PR2 authoring API).
export const upsertFlowDraftSchema =
  upsertFlowDraftObject.superRefine(refinePackageHeader);
export type UpsertFlowDraft = z.infer<typeof upsertFlowDraftSchema>;

// Body to create a brand-new flow (PR2 authoring API) — the draft fields plus
// the stable lookup `key` (lowercase slug, unique per tenant). `key` is optional:
// the server derives it from the name when omitted (deduped per tenant). When
// supplied it must be a lowercase slug and a duplicate is rejected (409).
export const createFlowRequestSchema = upsertFlowDraftObject
  .extend({
    key: z
      .string()
      .min(1)
      .regex(/^[a-z0-9_-]+$/, "key must be a lowercase slug (a-z, 0-9, -, _)")
      .optional(),
  })
  .superRefine(refinePackageHeader);
export type CreateFlowRequest = z.infer<typeof createFlowRequestSchema>;

// ---------------------------------------------------------------------------
// Assignment + materialized timeline DTOs
// ---------------------------------------------------------------------------

// Body to manually assign a flow to a patient. Accepts either a published flow
// row id (`flowId`) or a flow `flowKey` (the admin assigns by key — the server
// resolves the latest published version). Exactly one is required.
export const assignFlowRequestSchema = z
  .object({
    flowId: z.string().min(1).optional(),
    flowKey: z.string().min(1).optional(),
    // ISO datetime; defaults to now server-side (the day-0 anchor).
    startedAt: z.string().datetime().optional(),
    // TES-288: why the clinician starts this flow — persisted on the
    // assignment, audited, and shown in the admin. Optional in the schema
    // but ENFORCED server-side for the manual-assign endpoint (assignManual
    // rejects a missing/blank reason) so the governance requirement can't
    // be bypassed by calling the API directly.
    reason: z.string().trim().min(1).max(500).optional(),
    // TES-288: the published version the admin previewed — the server
    // rejects with 409 stale_flow_preview when a newer version was
    // published between preview and confirm.
    expectedVersion: z.number().int().positive().optional(),
  })
  .refine((d) => Boolean(d.flowId || d.flowKey), {
    message: "flowId or flowKey is required",
  });
export type AssignFlowRequest = z.infer<typeof assignFlowRequestSchema>;

export const flowAssignmentDtoSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  flowKey: z.string(),
  flowName: z.string(),
  flowVersion: z.number().int(),
  source: flowAssignmentSourceSchema,
  startedAt: z.string(),
  status: flowAssignmentStatusSchema,
  // TES-288: manual-activation governance — who started the flow and why.
  // Null for event-triggered assignments / legacy rows.
  assignedByName: z.string().nullable(),
  reason: z.string().nullable(),
});
export type FlowAssignmentDto = z.infer<typeof flowAssignmentDtoSchema>;

// One materialized item in a patient's upcoming timeline (admin, PR6) — carries
// the localized content so the admin can render a summary in either locale.
export const flowTimelineItemSchema = z.object({
  id: z.string(),
  flowItemId: z.string(),
  // TES-227 — the step's module type + its auto-complete event (if any), so the
  // calendar / home feed / Today surface can render + complete it correctly.
  moduleType: flowModuleTypeSchema.default("content"),
  completesOn: flowEventSchema.nullable().optional(),
  surfaces: z.array(flowSurfaceSchema),
  dueOn: z.string(),
  fireAt: z.string().nullable().optional(),
  timeOfDay: timeOfDaySchema.optional(),
  homeVisibility: flowHomeVisibilitySchema.nullable().optional(),
  status: flowAssignmentItemStatusSchema,
  // TES-285 — snapshotted per-module fields on the materialized instance, so
  // the priority engine / home feed / admin can rank + gate without re-reading
  // the template. Defaulted for pre-TES-285 rows.
  priority: z.number().int().min(1).max(100).default(50),
  durationDays: z.number().int().positive().nullable().optional(),
  required: z.boolean().default(false),
  blocksNextState: z.boolean().default(false),
  fallback: z.boolean().default(false),
  approvalStatus: flowApprovalStatusSchema.default("not_required"),
  // TES-287 — the patient task-state lifecycle + its timestamps. Defaulted 'ny'
  // for pre-TES-287 rows (the backfill flips historically-completed rows to
  // 'inskickad'). startedAt is set at `ny`→`påbörjad`, reviewedAt at →`granskad`.
  patientTaskState: patientTaskStateSchema.default("ny"),
  startedAt: z.string().nullable().optional(),
  reviewedAt: z.string().nullable().optional(),
  sourceFlowKey: z.string(),
  sourceFlowName: z.string(),
  content: flowItemContentSchema,
});
export type FlowTimelineItem = z.infer<typeof flowTimelineItemSchema>;

// Items grouped by their concrete due date.
export const flowTimelineDaySchema = z.object({
  dueOn: z.string(),
  items: z.array(flowTimelineItemSchema),
});
export type FlowTimelineDay = z.infer<typeof flowTimelineDaySchema>;

// GET /employee-api/v1/patients/:id/flows (PR6).
export const flowPatientOverviewSchema = z.object({
  assignments: z.array(flowAssignmentDtoSchema),
  timeline: z.array(flowTimelineDaySchema),
});
export type FlowPatientOverview = z.infer<typeof flowPatientOverviewSchema>;

// A patient-facing, locale-resolved home card (patient-api home feed, PR5).
export const flowHomeCardSchema = z.object({
  id: z.string(),
  flowKey: z.string(),
  // TES-231 — the step's module type drives where it renders on Home (task
  // section vs article card vs plain card) + whether it's tap-to-complete.
  moduleType: flowModuleTypeSchema.default("content"),
  completesOn: flowEventSchema.nullable().optional(),
  // Present for `article` steps — the referenced published article key.
  articleKey: z.string().nullable().optional(),
  title: z.string(),
  body: z.string(),
  ctaLabel: z.string().nullable().optional(),
  ctaPath: z.string().nullable().optional(),
  dueOn: z.string(),
  // TES (checklist): true once the item is completed. The home feed keeps a
  // completed task visible (checked + struck-through) for the rest of the day
  // it was completed, then drops it. Always false for non-completable steps.
  done: z.boolean().default(false),
  // TES-287 — the patient task-state, so the mobile task section can render
  // the `ny`/`påbörjad`/`inskickad`/`bokad`/`granskad` chip + resume a
  // `påbörjad` draft. 'ny' for non-completable steps (consumers gate on
  // moduleType).
  patientTaskState: patientTaskStateSchema.default("ny"),
});
export type FlowHomeCard = z.infer<typeof flowHomeCardSchema>;

// ---------------------------------------------------------------------------
// TES-287 — form-draft persistence (makes `påbörjad` reachable)
// ---------------------------------------------------------------------------

// Autosave a partial answer set for a completable flow item. `answers` is an
// opaque key→value bag (the mobile form owns its shape); saving it the first
// time transitions the item `ny`→`påbörjad` + stamps startedAt. PHI-bearing —
// stored server-side against the item, patient-scoped (RLS).
export const saveFlowItemDraftSchema = z.object({
  answers: z.record(z.unknown()),
});
export type SaveFlowItemDraft = z.infer<typeof saveFlowItemDraftSchema>;

export const flowItemDraftDtoSchema = z.object({
  itemId: z.string(),
  patientTaskState: patientTaskStateSchema,
  answers: z.record(z.unknown()),
  startedAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type FlowItemDraftDto = z.infer<typeof flowItemDraftDtoSchema>;

// ---------------------------------------------------------------------------
// TES-233 — flow-generated chat-message drafts + clinician approval queue
// ---------------------------------------------------------------------------

export const FLOW_CHAT_DRAFT_STATUSES = [
  "pending_approval",
  "sent",
  "rejected",
] as const;
export const flowChatMessageDraftStatusSchema = z.enum(
  FLOW_CHAT_DRAFT_STATUSES,
);
export type FlowChatMessageDraftStatus = z.infer<
  typeof flowChatMessageDraftStatusSchema
>;

// A draft in the clinician approval queue (employee-api).
export const flowChatMessageDraftDtoSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  // Who the draft is addressed to (see `patientLabelSchema`). Optional so an
  // older API response still parses; the admin falls back to the id fragment.
  patientName: z.string().nullable().optional(),
  personalNumberLast6: z.string().optional(),
  attribution: flowChatAttributionSchema,
  generationStrategy: flowChatGenerationStrategySchema,
  body: z.string(),
  status: flowChatMessageDraftStatusSchema,
  createdAt: z.string(),
});
export type FlowChatMessageDraftDto = z.infer<
  typeof flowChatMessageDraftDtoSchema
>;

// Edit a pending draft's body before approving.
export const editChatMessageDraftSchema = z.object({
  body: z.string().min(1),
});
export type EditChatMessageDraft = z.infer<typeof editChatMessageDraftSchema>;

// Reject a pending draft (optional reason for the audit trail).
export const rejectChatMessageDraftSchema = z.object({
  reason: z.string().optional(),
});
export type RejectChatMessageDraft = z.infer<
  typeof rejectChatMessageDraftSchema
>;
